import {
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isSuccess,
} from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { useNavigate, useParams } from '@/router';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useDeleteApInvoiceTransactionGeneralInput } from 'ap-openapi';
import { useCallback, useEffect } from 'react';

export type TransactionItemState = {
  transaction_item_deleted?: boolean;
};

export enum TransactionItemStatus {
  SUCCESS = 'success',
  INVALID = 'invalid',
  AUTH_ERROR = 'auth_error',
  ERROR = 'error',
}

export const useTransactionItemDeleteNotification = (
  state: TransactionItemState | null
) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { report_type_id } = useParams(
    '/report_types/:report_type_id/transaction_items'
  );

  useEffect(() => {
    if (state?.transaction_item_deleted && report_type_id) {
      notify.success({
        title: t('transaction_items.delete.success'),
        duration: 5,
      });
      navigate('/report_types/:report_type_id/transaction_items', {
        params: { report_type_id },
        state: null,
      });
    }
  }, [state, notify, t, navigate, report_type_id]);
};

export const useDeleteTransactionItem = () => {
  const { mutateAsync: deleteTransactionItem } =
    useDeleteApInvoiceTransactionGeneralInput();

  return useCallback(
    async (transaction_item_id: string = '') => {
      const resp = await deleteTransactionItem({ id: transaction_item_id });

      if (isSuccess(resp.status)) {
        return { status: TransactionItemStatus.SUCCESS };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        return {
          status: TransactionItemStatus.AUTH_ERROR,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else if (isInvalidError(resp.status)) {
        return {
          status: TransactionItemStatus.INVALID,
          errors: resp.data,
        };
      } else {
        return { status: TransactionItemStatus.ERROR };
      }
    },
    [deleteTransactionItem]
  );
};
